import { WizardFormBuilderInput } from "@CommonLib/src/frontend/WizardForm/Builder/WizardFormBuilderInput";
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	Divider,
	Typography,
} from "@mui/material";
import {
	BooleanInput,
	NumberInput,
	SaveButton,
	SimpleForm,
	TextInput,
	Toolbar,
} from "react-admin";
import { match } from "ts-pattern";

type ContextType = "create" | "create_public" | "edit";

interface FormComponentProps {
	type: ContextType;
}

const initialDisclaimerLabel = `Optional text shown to responders before they enter their email. You should write here any prerequisite
(required skills, files to upload etc) to ensure the responder is ready when the form starts (link is single-use).`;

const MainArea = (props: FormComponentProps) => {
	const { type } = props;
	return match(type)
		.with("create", () => (
			<Box mb={3}>
				<TextInput source="name" label="Form Name" fullWidth required />
				<NumberInput
					source="max_allowed_time"
					label="Max time to complete the form (minutes)"
					defaultValue={10}
					min={1}
					required
					helperText="Maximum time in minutes to complete this form (empty = no limit)"
				/>
				<BooleanInput
					source="auth_linkedin"
					label="Enable LinkedIn Authentication"
					defaultValue={false}
				/>
				<TextInput
					source="auth_restricted_email_domain"
					label="Restrict responses to specific email domain (optional)"
					helperText='e.g., "company.com" - only emails from this domain can respond'
					fullWidth
				/>
				<TextInput
					source="initial_disclaimer"
					InputLabelProps={{
						sx: {
							whiteSpace: "normal",
							lineHeight: "1.2",
						},
					}}
					label={initialDisclaimerLabel}
					fullWidth
					multiline
					minRows={4}
				/>
				<BooleanInput source="is_published" label="Is Published" />
			</Box>
		))
		.with("create_public", () => (
			<Box mb={3}>
				<TextInput source="name" label="Form Name" fullWidth required />
			</Box>
		))
		.with("edit", () => (
			<Box mb={3}>
				<TextInput source="name" label="Form Name" fullWidth required />
				<NumberInput
					source="max_allowed_time"
					label="Max time to complete the form (minutes)"
					defaultValue={10}
					min={1}
					required
					helperText="Maximum time in minutes to complete this form (empty = no limit)"
				/>
				<BooleanInput source="is_published" label="Is Published" />
				<BooleanInput
					source="send_response_email"
					label="Send me an email when someone responds"
				/>
				<BooleanInput
					source="auth_linkedin"
					label="Enable LinkedIn Authentication"
				/>
				<TextInput
					source="auth_restricted_email_domain"
					label="Restrict responses to specific email domain (optional)"
					helperText='e.g., "company.com" - only emails from this domain can respond'
					fullWidth
				/>
				<TextInput
					InputLabelProps={{
						sx: {
							whiteSpace: "normal",
							lineHeight: "1.2",
						},
					}}
					source="initial_disclaimer"
					label={initialDisclaimerLabel}
					fullWidth
					multiline
					minRows={4}
				/>
			</Box>
		))
		.exhaustive();
};

const BottomArea = (props: FormComponentProps) => {
	const { type } = props;
	return match(type)
		.with("create", () => <></>)
		.with("create_public", () => (
			<>
				<Box mb={2}>
					<Typography variant="body2" color="text.secondary">
						<strong>Important:</strong>
						If you already have a Nomachine account tied to this email,
						submitting here won’t create a new form (spam protection).
						<br />
						If that's the case, please log in to your account to create forms
						there.
						<br />
						If you don’t yet have a Nomachine account, you can safely proceed.
					</Typography>
				</Box>
				<Box mb={2}>
					<TextInput
						source="email"
						label="Your Email"
						type="email"
						fullWidth
						required
					/>
				</Box>
			</>
		))
		.with("edit", () => <></>)
		.exhaustive();
};

const generateTitle = (type: ContextType) => {
	return match(type)
		.with("create", () => "Create a new form")
		.with("create_public", () => "Create a new form")
		.with("edit", () => "Edit your form")
		.exhaustive();
};

export const FormComponent = (props: FormComponentProps) => {
	const { type } = props;
	return (
		<SimpleForm
			toolbar={
				<Toolbar>
					<SaveButton alwaysEnable />
				</Toolbar>
			}
			defaultValues={{ config: { steps: [] } }}
			sx={{
				"& .MuiPaper-root": {
					maxWidth: "none", // remove the default 52em
					width: "100%", // stretch across the parent
				},
			}}
		>
			<Card>
				<CardHeader
					title={generateTitle(type)}
					titleTypographyProps={{ variant: "h5" }}
				/>
				<Divider />

				<CardContent>
					<MainArea type={type} />
					<Box mb={3}>
						<WizardFormBuilderInput source="config" />
					</Box>

					<BottomArea type={type} />
				</CardContent>
			</Card>
		</SimpleForm>
	);
};
